import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';

import {
  addItemsToList, createList, fetchListsForPicker, removeItemsFromList,
} from '../../redux/features/lists/listsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';

const PAGE_SIZE = 10;

/**
 * Everything the list picker does: load the recent lists a page at a time,
 * search them, and toggle the target items in and out of one.
 *
 * A toggle applies optimistically and reverts if the request fails, so the row
 * never sits in a state the server didn't accept. Both the recent list and the
 * search run behind a request id, because a slow response that lands after a
 * newer one would otherwise overwrite it.
 *
 * `itemIds` puts the picker in bulk mode and `item` is ignored; bulk starts with
 * nothing checked, since the items in a selection need not agree on membership.
 */
export default function useListPicker({
  open, item, itemIds, onMembershipChange,
}) {
  const dispatch = useDispatch();

  const bulk = Array.isArray(itemIds) && itemIds.length > 0;
  const targetIds = bulk ? itemIds : [item?.id].filter(Boolean);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [recentLists, setRecentLists] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [added, setAdded] = useState(() => new Set());
  const [toggleCount, setToggleCount] = useState(0);

  const searchRequestId = useRef(0);
  const recentRequestId = useRef(0);

  // In single mode pre-check the lists the item already belongs to.
  // In bulk mode different items may have different memberships, so we
  // start with an empty set — the user sees session-only state.
  const initialMembership = useMemo(() => {
    const set = new Set();
    if (bulk) return set;
    const lists = item?.privateData?.lists || item?.publicData?.lists || [];
    lists.forEach((id) => set.add(id));
    return set;
  }, [item, bulk]);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setShowCreate(false);
    setNewListName('');
    setAdded(new Set(initialMembership));
    setToggleCount(0);
    setVisibleCount(PAGE_SIZE);
    setHasMore(true);
    setRecentLists([]);
    setSearchResults([]);
  }, [open, initialMembership]);

  // Debounce search input. Empty / single-char queries skip the wait so
  // clearing or starting to type feels immediate; longer queries wait 400ms
  // so we don't fire a request on every keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    const effective = trimmed.length < 2 ? '' : trimmed;
    const delay = effective ? 400 : 0;
    const t = setTimeout(() => setDebouncedQuery(effective), delay);
    return () => clearTimeout(t);
  }, [query, open]);

  // Fetch first page of recent lists on open
  useEffect(() => {
    if (!open) return;
    const reqId = recentRequestId.current + 1;
    recentRequestId.current = reqId;
    setLoadingRecent(true);
    dispatch(fetchListsForPicker({
      namespace: 'private',
      params: { offset: 0, limit: PAGE_SIZE, sortby: 'latest' },
    }))
      .unwrap()
      .then((res) => {
        if (recentRequestId.current !== reqId) return;
        const lists = res?.lists || [];
        setRecentLists(lists);
        setVisibleCount(lists.length);
        setHasMore(lists.length === PAGE_SIZE);
      })
      .catch((err) => {
        if (recentRequestId.current !== reqId) return;
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to load lists: ${err.message}`,
        }));
      })
      .finally(() => {
        if (recentRequestId.current === reqId) setLoadingRecent(false);
      });
  }, [open, dispatch]);

  // Search on debounced query change
  useEffect(() => {
    if (!open) return;
    if (!debouncedQuery) {
      setSearchResults([]);
      setLoadingSearch(false);
      return;
    }
    const reqId = searchRequestId.current + 1;
    searchRequestId.current = reqId;
    setLoadingSearch(true);
    dispatch(fetchListsForPicker({
      namespace: 'private',
      params: {
        offset: 0, limit: PAGE_SIZE, filter: debouncedQuery, sortby: 'latest',
      },
    }))
      .unwrap()
      .then((res) => {
        if (searchRequestId.current !== reqId) return;
        setSearchResults(res?.lists || []);
      })
      .catch((err) => {
        if (searchRequestId.current !== reqId) return;
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Search failed: ${err.message}`,
        }));
      })
      .finally(() => {
        if (searchRequestId.current === reqId) setLoadingSearch(false);
      });
  }, [debouncedQuery, open, dispatch]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    dispatch(fetchListsForPicker({
      namespace: 'private',
      params: { offset: visibleCount, limit: PAGE_SIZE, sortby: 'latest' },
    }))
      .unwrap()
      .then((res) => {
        const lists = res?.lists || [];
        setRecentLists((prev) => {
          const seen = new Set(prev.map((l) => l.id));
          const merged = [...prev];
          lists.forEach((l) => {
            if (!seen.has(l.id)) merged.push(l);
          });
          return merged;
        });
        setVisibleCount((c) => c + lists.length);
        if (lists.length < PAGE_SIZE) setHasMore(false);
      })
      .catch((err) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to load more: ${err.message}`,
        }));
      })
      .finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, visibleCount, dispatch]);

  const toggleList = useCallback((listInfo) => {
    if (!targetIds.length || !listInfo?.id) return;
    const listId = listInfo.id;
    const isAdded = added.has(listId);
    const next = new Set(added);
    if (isAdded) next.delete(listId);
    else next.add(listId);
    setAdded(next);
    setToggleCount((c) => c + 1);

    const op = dispatch((isAdded ? removeItemsFromList : addItemsToList)({
      namespace: 'private',
      listId,
      items: targetIds.map((id) => ({ id })),
    })).unwrap();

    op
      .then(() => {
        if (onMembershipChange) onMembershipChange(isAdded ? 'remove' : 'add', listInfo);
      })
      .catch((err) => {
        // revert on failure
        setAdded((cur) => {
          const reverted = new Set(cur);
          if (isAdded) reverted.add(listId);
          else reverted.delete(listId);
          return reverted;
        });
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: isAdded
            ? `Failed to remove from list: ${err.message}`
            : `Failed to add to list: ${err.message}`,
        }));
      });
  }, [added, targetIds, dispatch, onMembershipChange]);

  const handleCreate = useCallback(() => {
    const name = newListName.trim();
    if (!name || creating || !targetIds.length) return;
    setCreating(true);
    dispatch(createList({ namespace: 'private', listInfo: { title: name } }))
      .unwrap()
      .then((created) => dispatch(addItemsToList({
        namespace: 'private',
        listId: created.id,
        items: targetIds.map((id) => ({ id })),
      })).unwrap().then(() => created))
      .then((created) => {
        // The list returned from createList is typically `{ id, privateData: {...} }`.
        // Normalize for display in the row list.
        const normalized = created?.privateData || created?.publicData
          ? created
          : { id: created.id, privateData: { ...created } };
        setRecentLists((prev) => {
          const filtered = prev.filter((l) => l.id !== normalized.id);
          return [normalized, ...filtered];
        });
        setVisibleCount((c) => c + 1);
        setAdded((cur) => {
          const next = new Set(cur);
          next.add(normalized.id);
          return next;
        });
        setToggleCount((c) => c + 1);
        setShowCreate(false);
        setNewListName('');
        setQuery('');
        setDebouncedQuery('');
        if (onMembershipChange) onMembershipChange('add', normalized);
      })
      .catch((err) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to create list: ${err.message}`,
        }));
      })
      .finally(() => setCreating(false));
  }, [newListName, creating, targetIds, dispatch, onMembershipChange]);

  const openCreate = useCallback(() => {
    setNewListName(query.trim());
    setShowCreate(true);
  }, [query]);

  const cancelCreate = useCallback(() => {
    setShowCreate(false);
    setNewListName('');
  }, []);

  const searching = debouncedQuery.length > 0;
  const visibleLists = searching
    ? searchResults
    : recentLists.slice(0, visibleCount);

  return {
    bulk,
    bulkCount: targetIds.length,
    query,
    setQuery,
    debouncedQuery,
    searching,
    visibleLists,
    added,
    toggleList,
    toggleCount,
    hasMore,
    loadMore,
    loadingMore,
    loadingRecent,
    loadingSearch,
    // The spinner shows only when there is nothing to show behind it; a refresh
    // over existing rows leaves them in place.
    initialLoading: loadingRecent && !recentLists.length,
    searchInitialLoading: searching && loadingSearch && !searchResults.length,
    showCreate,
    openCreate,
    cancelCreate,
    newListName,
    setNewListName,
    creating,
    handleCreate,
  };
}
